import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function postWithRetry(url, body, retries = 3, delayMs = 1500) {
  for (let i = 0; i < retries; i++) {
    try {
      return await axios.post(url, body);
    } catch (err) {
      const status = err.response?.status;
      if (i < retries - 1 && (!status || status >= 502)) {
        await new Promise(r => setTimeout(r, delayMs));
        continue;
      }
      throw err;
    }
  }
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuthFromGoogle } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const error = params.get('error');

        if (error) {
          console.error('Google OAuth error:', error);
          navigate('/login', { replace: true, state: { error: 'Google sign-in was cancelled.' } });
          return;
        }

        if (!code) {
          navigate('/login', { replace: true });
          return;
        }

        const redirect_uri = window.location.origin + '/auth/callback';
        const { data } = await postWithRetry(`${API}/auth/google/callback`, {
          code,
          redirect_uri,
        });

        setAuthFromGoogle(data);

        try {
          const { data: profile } = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${data.access_token}` },
          });
          const ob = profile.onboarding || {};
          if (ob.complete !== true && profile.onboarding_complete !== true) {
            navigate('/onboarding', { replace: true });
            return;
          }
        } catch { /* fallback: let ProtectedRoute handle it */ }

        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('Google auth failed:', err);
        navigate('/login', { replace: true, state: { error: 'Google sign-in failed. Please try again.' } });
      }
    };

    processCallback();
  }, [navigate, location.search, setAuthFromGoogle]);

  return (
    <div className="flex items-center justify-center h-screen bg-background" data-testid="auth-callback">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent mx-auto mb-4" />
        <p className="text-sm text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
